import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
} from '@mui/material';
import { Send, Notifications, People, Storefront } from '@mui/icons-material';
import { sendPushNotification } from '../../services/Api';

const PushNotifications = () => {
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        userType: 'user'
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        if (!/^[a-zA-Z0-9\s]+$/.test(formData.title.trim())) {
            setError('Notification title cannot contain special characters');
            setLoading(false);
            return;
        }

        if (/^\d+$/.test(formData.message.trim())) {
            setError('Message cannot contain only numeric values');
            setLoading(false);
            return;
        }

        try {
            const payload = {
                type: formData.userType.toLowerCase(),
                title: formData.title,
                message: formData.message,
            };

            const response = await sendPushNotification(payload);
            
            if (response.success) {
                setSuccess(true);
                setFormData({
                    title: '',
                    message: '',
                    userType: 'user'
                });
                setTimeout(() => setSuccess(false), 5000);
            } else {
                setError(response.message || 'Failed to send notification');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error sending notification');
        } finally {
            setLoading(false);
        }
    };

    const getUserTypeIcon = (userType) => {
        switch (userType) {
            case 'user':
                return <People />;
            case 'vendor':
                return <Storefront />;
            // case 'partner':
            //     return <DirectionsBike />;
            default:
                return <People />;
        }
    };

    const getUserTypeDescription = (userType) => {
        switch (userType) {
            case 'user':
                return 'Send notifications to all app users';
            case 'vendor':
                return 'Send notifications to all vendors/stores';
            // case 'partner':
            //     return 'Send notifications to all delivery partners';
            default:
                return 'Send notifications to all users';
        }
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ 
                    fontWeight: 300, 
                    display: 'flex', 
                    alignItems: 'center',
                    fontSize: { xs: '1.75rem', sm: '2.125rem' } 
                }}>
                    <Notifications sx={{ mr: 2, fontSize: { xs: 28, sm: 32 }, color: 'primary.main' }} />
                    Push Notifications
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Send push notifications to different user groups in the platform
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 4 }}>Notification sent successfully!</Alert>}

            <Grid container spacing={4}>
                {/* User Type Selection */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h6" fontWeight="600" mb={2}>Select Type For Whom</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {['user', 'vendor'].map((userType) => (
                            <Card
                                key={userType}
                                elevation={0}
                                sx={{
                                    border: formData.userType === userType ? '2px solid' : '1px solid',
                                    borderColor: formData.userType === userType ? 'primary.main' : 'text.border',
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }
                                }}
                                onClick={() => setFormData(prev => ({ ...prev, userType }))}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ 
                                        width: { xs: 40, sm: 48 }, 
                                        height: { xs: 40, sm: 48 }, 
                                        borderRadius: '50%', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        bgcolor: formData.userType === userType ? 'primary.light' : 'grey.100',
                                        color: formData.userType === userType ? 'primary.contrastText' : 'text.secondary'
                                    }}>
                                        {getUserTypeIcon(userType)}
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="600" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                            {userType}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            {getUserTypeDescription(userType)}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </Grid>

                {/* Notification Form */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                        <Typography variant="h6" fontWeight="600" mb={3} sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                            Compose Notification for {formData.userType}
                        </Typography>
                        
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        label="Notification Title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter notification title"
                                        // helperText="Maximum 100 characters"
                                        // inputProps={{ maxLength: 100 }}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        label="Message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        required
                                        multiline
                                        rows={4}
                                        placeholder="Enter your notification message here..."
                                        // helperText="Maximum 500 characters"
                                        // inputProps={{ maxLength: 500 }}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        justifyContent: 'space-between', 
                                        alignItems: { xs: 'stretch', sm: 'center' }, 
                                        gap: { xs: 2, sm: 0 },
                                        mt: 2 
                                    }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                            This notification will be sent to all {formData.userType.toLowerCase()} in the platform
                                        </Typography>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                            disabled={loading || !formData.title.trim() || !formData.message.trim()}
                                            sx={{ 
                                                px: { xs: 2, sm: 4 },
                                                py: 1.2,
                                                backgroundColor: 'primary.main',
                                                fontWeight: 'bold',
                                                borderRadius: '12px',
                                                width: { xs: '100%', sm: 'auto' }
                                            }}
                                        >
                                            {loading ? 'Sending...' : 'Send Notification'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PushNotifications;
